import React from "react";
import { StyleSheet, View, ScrollView, Text } from "react-native";
import CustomModal from "../../common/general/CustomModal/CustomModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";

const ProjectDetailsModal = ({ visible, onClose, project, submission }) => {
  if (!project) return null;

  const formatDate = (date) => {
    if (!date) return "Not specified";
    return new Date(date).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "#12B76A";
      case "in_progress":
      case "active":
        return "#F79009";
      case "draft":
        return "#667085";
      default:
        return "#667085";
    }
  };

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      title="Project Details"
      showCloseButton={true}
      ariaLabel="Project details modal">
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false} tabIndex={0}>
        {/* Project Title and Status */}
        <View style={styles.section}>
          <CustomText style={styles.projectTitle} text={project.name} useTranslationText={false} />
          <View style={styles.statusContainer}>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(project.status) }]}>
              <CustomText
                style={styles.statusText}
                text={project.status?.toUpperCase() || "DRAFT"}
                useTranslationText={false}
              />
            </View>
          </View>
        </View>

        {/* Description */}
        {project.description && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText style={styles.sectionTitle} text="Description" useTranslationText={false} />
            </View>
            <CustomText
              style={styles.sectionContent}
              text={project.description.replace(/<[^>]*>/g, "")} // Strip HTML tags
              useTranslationText={false}
            />
          </View>
        )}

        {/* Scope Document */}
        {project.scopeDocument && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText style={styles.sectionTitle} text="Project Scope" useTranslationText={false} />
            </View>
            <CustomText style={styles.sectionContent} text={project.scopeDocument} useTranslationText={false} />
          </View>
        )}

        {/* Custom Instructions */}
        {project.customInstructions && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText
                style={styles.sectionTitle}
                text="Personalized Project Instructions"
                useTranslationText={false}
              />
            </View>
            <View style={styles.instructionsContainer}>
              <CustomText style={styles.sectionContent} text={project.customInstructions} useTranslationText={false} />
            </View>
          </View>
        )}

        {/* Deliverable Types */}
        {project.deliverableTypes && project.deliverableTypes.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText style={styles.sectionTitle} text="Deliverable Types" useTranslationText={false} />
            </View>
            <View style={styles.listContainer}>
              {project.deliverableTypes.map((deliverableType, index) => (
                <View key={index} style={styles.listItem}>
                  <View style={styles.bullet} />
                  <CustomText style={styles.listText} text={deliverableType} useTranslationText={true} />
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Assessment Criteria */}
        {project.assessmentCriteria && project.assessmentCriteria.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText style={styles.sectionTitle} text="Assessment Criteria" useTranslationText={false} />
            </View>
            <View style={styles.listContainer}>
              {project.assessmentCriteria.map((criteria, index) => (
                <View key={index} style={styles.listItem}>
                  <View style={styles.bullet} />
                  <CustomText style={styles.listText} text={criteria} useTranslationText={false} />
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Expected Outcomes */}
        {project.expectedOutcomes && project.expectedOutcomes.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText style={styles.sectionTitle} text="Expected Outcomes" useTranslationText={false} />
            </View>
            <View style={styles.listContainer}>
              {project.expectedOutcomes.map((outcome, index) => (
                <View key={index} style={styles.listItem}>
                  <View style={styles.bullet} />
                  <CustomText style={styles.listText} text={outcome} useTranslationText={false} />
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Project Stats */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <CustomText style={styles.sectionTitle} text="Project Information" useTranslationText={false} />
          </View>
          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <CustomText style={styles.statLabel} text="Total Tasks" useTranslationText={false} />
              <CustomText
                style={styles.statValue}
                text={`${submission?.requirements.length || 0}`}
                useTranslationText={false}
              />
            </View>
            <View style={styles.statItem}>
              <CustomText style={styles.statLabel} text="Completed Tasks" useTranslationText={false} />
              <CustomText
                style={styles.statValue}
                text={`${submission?.requirements.filter((req) => req.completed).length || 0}`}
                useTranslationText={false}
              />
            </View>
            <View style={styles.statItem}>
              <CustomText style={styles.statLabel} text="Due Date" />
              <CustomText style={styles.statValue} text={formatDate(project.dueDate)} useTranslationText={false} />
            </View>
          </View>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <CustomButton text="Close" size="m" styleType="primaryLight" onPress={onClose} style={styles.closeBtn} />
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingTop: 20,
    maxHeight: 600,
  },
  section: {
    marginBottom: 24,
  },
  projectTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#101828",
    marginBottom: 12,
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#ffffff",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#101828",
    marginLeft: 0,
  },
  sectionContent: {
    fontSize: 16,
    color: "#667085",
    lineHeight: 24,
  },
  instructionsContainer: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    padding: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#7C3AED",
  },
  listContainer: {
    marginTop: 8,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  bullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#667085",
    marginTop: 8,
    marginRight: 12,
  },
  listText: {
    fontSize: 16,
    color: "#667085",
    lineHeight: 24,
    flex: 1,
  },
  statsContainer: {
    marginTop: 8,
  },
  statItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F2F4F7",
  },
  statLabel: {
    fontSize: 16,
    color: "#667085",
    fontWeight: "500",
  },
  statValue: {
    fontSize: 16,
    color: "#101828",
    fontWeight: "600",
  },
  footer: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    marginTop: 16,
  },
  closeBtn: {
    borderColor: "#D0D5DD",
    borderWidth: 1,
  },
});

export default ProjectDetailsModal;
